/**
 * FormCommands
 *
 * Registers the active form's actions in the command palette so they can be run
 * by keyboard: send email, export PDF, close & create, each applicable workflow,
 * and each allowed state transition. Mounted by FormPageContent *inside*
 * FormWorkflowActionProvider (it must be a component, not a hook in
 * FormPageContent, because that page renders the provider and so sits outside
 * it). Renders nothing — it only registers commands for its lifetime, calling
 * the same context actions the form menus use, so palette and menu behave
 * identically.
 */

import { FC, useMemo } from 'react';
import { formStateDescription } from '#types';
import { useFormWorkflowAction } from '@/providers/artifacts/FormWorkflowActionContext';
import { isCloseAndCreateTargetState } from '@/use/form/isCloseAndCreateTargetState';
import { useRegisterCommands, type CommandDescriptor } from '@/keyboard';

const FormCommands: FC = () => {
  const {
    isCreating,
    applicableWorkflows,
    handleWorkflowClick,
    allowedNextStates,
    handleSetState,
    handleExport,
    handleEmailClick,
    onOpenCloseAndCreate,
    isCloseAndCreateEntryDisabled,
  } = useFormWorkflowAction();

  const entries = useMemo(() => {
    const list: { descriptor: CommandDescriptor; run: () => void }[] = [
      {
        descriptor: {
          id: 'cmd.form.email',
          label: 'Send via Email',
          group: 'Form',
          keywords: 'email send share mail',
          enabled: !isCreating,
        },
        run: handleEmailClick,
      },
      {
        descriptor: {
          id: 'cmd.form.pdf',
          label: 'Export as PDF',
          group: 'Form',
          keywords: 'pdf print download export',
          enabled: !isCreating,
        },
        run: handleExport,
      },
      {
        descriptor: {
          id: 'cmd.form.closeAndCreate',
          label: 'Close & Create',
          group: 'Form',
          keywords: 'close create next form',
          enabled: !isCloseAndCreateEntryDisabled,
        },
        run: onOpenCloseAndCreate,
      },
    ];

    for (const w of applicableWorkflows) {
      if (!w.docId) continue;
      list.push({
        descriptor: {
          id: `cmd.form.workflow.${w.docId}`,
          label: w.description?.shortLabel || w.docId,
          group: 'Workflows',
          keywords: `workflow run ${w.description?.shortLabel ?? ''}`,
          enabled: !isCreating,
        },
        run: () => {
          void handleWorkflowClick(w);
        },
      });
    }

    for (const state of (allowedNextStates ?? []).filter(isCloseAndCreateTargetState)) {
      list.push({
        descriptor: {
          id: `cmd.form.state.${state}`,
          label: `Set form to ${formStateDescription[state]?.shortLabel || state}`,
          group: 'Form state',
          keywords: `state status set change ${formStateDescription[state]?.shortLabel ?? ''}`,
          enabled: !isCreating,
        },
        run: () => {
          void handleSetState(state);
        },
      });
    }

    return list;
  }, [
    isCreating,
    isCloseAndCreateEntryDisabled,
    applicableWorkflows,
    allowedNextStates,
    handleEmailClick,
    handleExport,
    onOpenCloseAndCreate,
    handleWorkflowClick,
    handleSetState,
  ]);

  useRegisterCommands(entries);

  return null;
};

export default FormCommands;
